import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Suspense } from 'react';
import { Toaster } from '@/components/ui/toaster';
import { AuthProvider } from '@/hooks/useAuth';
import { DataCacheProvider } from '@/hooks/useDataCache';
import { ThemeProvider } from '@/components/ThemeProvider';
import ErrorBoundary from '@/components/ErrorBoundary';

// Pages
import LandingPage from '@/pages/LandingPage';
import SignupPage from '@/pages/SignupPage';
import TenantSelector from '@/pages/TenantSelector';
import LoginPage from '@/pages/LoginPage';
import AppLayout from '@/components/layouts/AppLayout';
import Dashboard from '@/pages/app/Dashboard';
import Users from '@/pages/app/Users';
import Roles from '@/pages/app/Roles';
import Company from '@/pages/app/Company';
import Audit from '@/pages/app/Audit';
import ProductionOrders from '@/pages/app/ProductionOrders';
import ProductionOrderPrintView from '@/pages/app/ProductionOrderPrintView';
import EngravingOrderPrintView from '@/pages/app/EngravingOrderPrintView';
import CoextrusionPrintView from '@/pages/app/CoextrusionPrintView';
import PlanningBoardView from '@/pages/app/PlanningBoardView';
import PlanningSimulations from '@/pages/app/PlanningSimulations';
import MasterListView from '@/pages/app/MasterListView';
import PlantExecution from '@/pages/app/PlantExecution';
import Visor360RT from '@/pages/app/Visor360RT';
import Settings from '@/pages/app/Settings';
import CatalogsView from '@/pages/app/CatalogsView';
import ProductionClosure from '@/pages/app/ProductionClosure';
import DesignModule from '@/pages/app/DesignModule';
import OrdersPurge from '@/pages/app/OrdersPurge';
import ComerciaOrderSync from '@/pages/app/ComerciaOrderSync';
import IndicadoresView from '@/pages/app/IndicadoresView';

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 3,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

// Loading fallback component
const LoadingFallback = () => (
  <div className="min-h-screen bg-background flex items-center justify-center">
    <div className="text-center">
      <div className="w-12 h-12 flex items-center justify-center mx-auto mb-4">
        <img src="/qpulse-logo.png" alt="QPulseMES" className="w-12 h-12 object-contain" />
      </div>
      <p className="text-gray-600">Cargando...</p>
    </div>
  </div>
);

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider defaultTheme="light" storageKey="qpulsemes-ui-theme">
        <Suspense fallback={<LoadingFallback />}>
          <Router>
            <AuthProvider>
              <DataCacheProvider>
                <div className="min-h-screen bg-background">
                <Routes>
                  {/* Public routes */}
                  <Route path="/" element={<LandingPage />} />
                  <Route path="/signup" element={<SignupPage />} />
                  <Route path="/login" element={<TenantSelector />} />
                  
                  {/* Tenant login: /[nombretenant]/login/ */}
                  <Route path="/:tenant/login" element={<LoginPage />} />
                  
                  {/* Print views - standalone without layout */}
                  <Route path="/app/production-orders/:id/print" element={<ProductionOrderPrintView />} />
                  <Route path="/app/engraving-orders/:id/print" element={<EngravingOrderPrintView />} />
                  <Route path="/app/coextrusion-orders/:productionOrderId/print" element={<CoextrusionPrintView />} />
                  
                  {/* Protected app routes */}
                  <Route path="/app" element={<AppLayout />}>
                    <Route index element={<Navigate to="/app/dashboard" replace />} />
                    <Route path="dashboard" element={<ErrorBoundary><Dashboard /></ErrorBoundary>} />
                    <Route path="production-orders" element={<ErrorBoundary><ProductionOrders /></ErrorBoundary>} />
                    <Route path="planning-board" element={<ErrorBoundary><PlanningBoardView /></ErrorBoundary>} />
                    <Route path="planning-simulations" element={<ErrorBoundary><PlanningSimulations /></ErrorBoundary>} />
                    <Route path="plant-execution" element={<ErrorBoundary><PlantExecution /></ErrorBoundary>} />
                    <Route path="visor-360" element={<ErrorBoundary><Visor360RT /></ErrorBoundary>} />
                    <Route path="master-list" element={<ErrorBoundary><MasterListView /></ErrorBoundary>} />
                    <Route path="catalogs" element={<ErrorBoundary><CatalogsView /></ErrorBoundary>} />
                    <Route path="production-closure" element={<ErrorBoundary><ProductionClosure /></ErrorBoundary>} />
                    <Route path="diseno" element={<ErrorBoundary><DesignModule /></ErrorBoundary>} />
                    <Route path="users" element={<ErrorBoundary><Users /></ErrorBoundary>} />
                    <Route path="roles" element={<ErrorBoundary><Roles /></ErrorBoundary>} />
                    <Route path="company" element={<ErrorBoundary><Company /></ErrorBoundary>} />
                    <Route path="audit" element={<ErrorBoundary><Audit /></ErrorBoundary>} />
                    <Route path="settings" element={<ErrorBoundary><Settings /></ErrorBoundary>} />
                    <Route path="comercia-sync" element={<ErrorBoundary><ComerciaOrderSync /></ErrorBoundary>} />
                    <Route path="orders-purge" element={<ErrorBoundary><OrdersPurge /></ErrorBoundary>} />
                    <Route path="indicadores" element={<ErrorBoundary><IndicadoresView /></ErrorBoundary>} />
                  </Route>
                  
                  {/* Catch all */}
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
                </div>
                <Toaster />
              </DataCacheProvider>
            </AuthProvider>
          </Router>
        </Suspense>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;